import { useParams } from "react-router-dom";
import { useFilters } from "../context/FilterContext";
import { useFetch } from "../lib/useFetch";
import { PageHeader, Panel, Stat, Loading, Chip } from "../components/common/ui";
import { SubTabs } from "../components/common/SubTabs";
import { AreaTrend, Bars, Donut } from "../components/common/Charts";
import { cn } from "../lib/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const TABS = [
  { key: "quality", label: "Quality" }, { key: "complaints", label: "Complaints" },
  { key: "sla", label: "SLA" }, { key: "escalations", label: "Escalations" },
];

const sev = (s) => ({ High: "bg-red-100 text-red-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-slate-100 text-slate-600" }[s] || "");
const stat = (s) => ({ Open: "bg-blue-100 text-blue-700", Resolved: "bg-emerald-100 text-emerald-700", Escalated: "bg-red-100 text-red-700" }[s] || "");

export default function QualitySLA() {
  const { tab = "quality" } = useParams();
  const { range, hub } = useFilters();
  return (
    <div data-testid="quality-page">
      <PageHeader title="Quality & SLA" subtitle="Quality control, complaint analytics, SLA performance and escalations." />
      <SubTabs base="/quality" tab={tab} items={TABS} />
      {tab === "quality" && <QualityTab range={range} hub={hub} />}
      {tab === "complaints" && <Complaints hub={hub} />}
      {tab === "sla" && <SLA range={range} />}
      {tab === "escalations" && <Escalations />}
    </div>
  );
}

function QualityTab({ range, hub }) {
  const { data, loading } = useFetch(`/quality/overview?range=${range}&hub=${hub}`, [range, hub]);
  if (loading || !data) return <Loading rows={3} />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Stat label="Quality Score" value={`${data.quality_score}%`} tone="text-primary" />
        <Stat label="Rewash Rate" value={`${data.rewash_rate}%`} />
        <Stat label="Damage Rate" value={`${data.damage_rate}%`} />
        <Stat label="Missing Item" value={`${data.missing_item_rate}%`} />
        <Stat label="Complaint Rate" value={`${data.complaint_rate}%`} tone="text-amber-600" />
        <Stat label="Refund Rate" value={`${data.refund_rate}%`} />
        <Stat label="Inspection Pass" value={`${data.inspection_pass}%`} tone="text-emerald-600" />
      </div>
      <Panel title="Complaints Trend"><AreaTrend data={data.trend} color="#EF4444" height={280} /></Panel>
    </div>
  );
}

function Complaints({ hub }) {
  const { data, loading } = useFetch(`/quality/complaints?hub=${hub}`, [hub]);
  if (loading || !data) return <Loading rows={4} />;
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Complaints by Category"><Donut data={data.categories} height={300} /></Panel>
        <Panel title="Category Breakdown"><Bars data={data.categories} horizontal height={300} /></Panel>
      </div>
      <Panel title={`Complaints (${data.total})`} subtitle="Drill: category → hub → order → process → root cause">
        <div className="max-h-[420px] overflow-y-auto scrollbar-thin">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Hub</TableHead>
                <TableHead>Order</TableHead><TableHead>Process</TableHead><TableHead>Root Cause</TableHead>
                <TableHead>Severity</TableHead><TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.list.map((c) => (
                <TableRow key={c.id} className="hover:bg-slate-50">
                  <TableCell className="text-slate-500">{c.date}</TableCell>
                  <TableCell className="font-medium">{c.category}</TableCell>
                  <TableCell className="text-slate-500">{c.hub_name}</TableCell>
                  <TableCell className="text-slate-500">{c.order_id}</TableCell>
                  <TableCell className="text-slate-500">{c.factory_process}</TableCell>
                  <TableCell className="text-slate-500">{c.root_cause}</TableCell>
                  <TableCell><Chip className={sev(c.severity)}>{c.severity}</Chip></TableCell>
                  <TableCell><Chip className={stat(c.status)}>{c.status}</Chip></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Panel>
    </div>
  );
}

function SLA({ range }) {
  const { data, loading } = useFetch(`/quality/sla?range=${range}`, [range]);
  if (loading || !data) return <Loading rows={4} />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Pickup SLA" value={`${data.pickup_sla}%`} />
        <Stat label="Processing SLA" value={`${data.processing_sla}%`} />
        <Stat label="Delivery SLA" value={`${data.delivery_sla}%`} tone={data.delivery_sla < 95 ? "text-red-600" : ""} />
        <Stat label="End-to-End" value={`${data.end_to_end_sla}%`} />
        <Stat label="Breach %" value={`${data.breach_pct}%`} tone="text-amber-600" />
        <Stat label="Avg Delay" value={`${data.avg_delay_hr}h`} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Worst Performing Hubs (SLA)">
          <div className="space-y-2">
            {data.worst_hubs.map((h) => (
              <div key={h.name} className="flex items-center justify-between text-sm border border-slate-100 rounded-md px-3 py-2">
                <span className="font-medium">{h.name}</span>
                <span className={cn("font-semibold", h.sla < 95 ? "text-red-600" : "text-slate-600")}>{h.sla}%</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Delay Reasons"><Bars data={data.delay_reasons.map((r) => ({ name: r.reason, value: r.value }))} horizontal height={240} /></Panel>
      </div>
      <Panel title="On-Time Deliveries Trend"><AreaTrend data={data.trend} color="#6E3667" height={260} /></Panel>
    </div>
  );
}

function Escalations() {
  const { data, loading } = useFetch("/quality/escalations", []);
  if (loading || !data) return <Loading rows={4} />;
  return (
    <Panel title={`Escalations (${data.total})`} subtitle="Complaints escalated beyond hub level">
      <Table>
        <TableHeader>
          <TableRow><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Hub</TableHead><TableHead>Customer</TableHead><TableHead>Root Cause</TableHead><TableHead>Severity</TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {data.list.map((c) => (
            <TableRow key={c.id} className="hover:bg-slate-50">
              <TableCell className="text-slate-500">{c.date}</TableCell>
              <TableCell className="font-medium">{c.category}</TableCell>
              <TableCell className="text-slate-500">{c.hub_name}</TableCell>
              <TableCell className="text-slate-500">{c.customer}</TableCell>
              <TableCell className="text-slate-500">{c.root_cause}</TableCell>
              <TableCell><Chip className={sev(c.severity)}>{c.severity}</Chip></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}
